/**
 * @file Список доступных пользователю хранилищ.
 *
 * Различает загрузку, ошибку и корректный пустой список. revision меняется
 * по кнопке обновления и запускает новое чтение в useEffect.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useCustomer } from '../../context/useCustomer'
import type { CustomerBox } from '../../context/useCustomer'
import { useLocale } from '../../i18n/useLocale'
import { fetchSession } from '../session/api'
import './StorageListPage.css'
import { storageMessages } from './messages'

type ListState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; storages: CustomerBox[] }

export default function StorageListPage() {
    const { setCustomer } = useCustomer()
    const { locale } = useLocale()
    const text = storageMessages[locale]

    // Результат чтения и номер ручного обновления разделены: revision запускает эффект.
    const [state, setState] = useState<ListState>({ status: 'loading' })
    const [revision, setRevision] = useState(0)

    useEffect(() => {
        document.title = `${text.title} · Cloud-box`
    }, [text.title])

    useEffect(() => {
        const controller = new AbortController()
        fetchSession(
            AbortSignal.any([controller.signal, AbortSignal.timeout(30_000)]),
        )
            .then((customer) => {
                if (controller.signal.aborted) return

                setCustomer(customer)
                setState({ status: 'ready', storages: customer.boxes })
            })
            .catch(() => {
                if (!controller.signal.aborted) setState({ status: 'error' })
            })

        return () => controller.abort()
    }, [revision, setCustomer])

    function refresh() {
        setState({ status: 'loading' })
        setRevision((value) => value + 1)
    }

    return (
        <section className='storage-list' aria-labelledby='storage-list-title'>
            <header className='storage-list-header'>
                <div>
                    <h1 id='storage-list-title'>{text.title}</h1>

                    <p>{text.description}</p>
                </div>

                <button
                    className='storage-list-refresh'
                    onClick={refresh}
                    disabled={state.status === 'loading'}
                >
                    {text.refresh}
                </button>
            </header>

            {state.status === 'loading' && (
                <div className='storage-list-state' role='status'>
                    {text.loading}
                </div>
            )}
            {state.status === 'error' && (
                <div className='storage-list-state'>
                    <div role='alert'>
                        <h2>{text.errorTitle}</h2>

                        <p>{text.errorDescription}</p>
                    </div>

                    <button className='storage-list-refresh' onClick={refresh}>
                        {text.retry}
                    </button>
                </div>
            )}
            {state.status === 'ready' &&
                (state.storages.length === 0 ? (
                    <div className='storage-list-state' role='status'>
                        <h2>{text.emptyTitle}</h2>

                        <p>{text.emptyDescription}</p>
                    </div>
                ) : (
                    <ul className='storage-list-grid'>
                        {state.storages.map((storage) => (
                            <li key={storage.id}>
                                <Link
                                    className='storage-list-card'
                                    to={`/dashboard/box/${encodeURIComponent(storage.id)}`}
                                >
                                    <svg
                                        className='storage-list-icon'
                                        width='28'
                                        height='28'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='1.5'
                                        aria-hidden='true'
                                    >
                                        <rect
                                            x='3'
                                            y='4'
                                            width='18'
                                            height='16'
                                            rx='3'
                                        />
                                        <path
                                            d='M3 12h18M7 8h.01M7 16h.01'
                                            strokeLinecap='round'
                                        />
                                    </svg>

                                    <h2>{storage.id}</h2>

                                    <span>
                                        {text.open}
                                        <span aria-hidden='true'> →</span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                ))}
        </section>
    )
}
